import React, { FC, useState, useEffect, useCallback, useRef } from 'react'
import { useDispatch, } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
    Space, Card, List, Descriptions, DatePicker, DatePickerProps,
    Modal, Table, TableProps, Button, Image, Spin, Skeleton
} from 'antd'
import 'moment/locale/zh-cn'
import { marked } from 'marked'
import type { Moment } from 'moment'
import moment from 'moment'
import PageBreadcrumb from '@/components/pageBreadcrumb'
import { DefaultPlugin } from '@dslate/dslate'
import { getTechInfo, getTechInfoList, getFaceBackList, getFaceBackReport } from '@/services/vote.service'
import Nav from '@/components/nav'

const plugs = { ...DefaultPlugin }
// 删除图片工具
Reflect.deleteProperty(plugs, 'ImgPlugin')

interface TableDataType {
    id: string
    isApproval: boolean
    techAbstract: string
    $tokens?: any[]
    techContext?: string
    techUrl?: string
    creator?: string
    createTime?: Date
    remark?: string
}

const renderer = new marked.Renderer()
const { ...defaults } = marked.getDefaults()

const getHtmlStr = (html: string | null) => {
    if (html) {
        let htmlStr = html
        if (html.includes('obj') && html.includes('str')) {
            const obj = JSON.parse(html)
            htmlStr = obj.str
        }
        const word = htmlStr?.replace(/<[^>]*>/g, '').replace(/\\n/g, '<br/>')
        return word
    } else {
        return ''
    }
}

const ViewHtml = (props: {
    open: boolean
    html: any[]
    url?: string
    sourceUrl?: string
    loading: boolean
    close: () => void
}) => {
    const { open, html: tokens, url, sourceUrl, loading, close } = props
    const [, setIframeKey] = useState(0)
    useEffect(() => {
        if (open) {
            setIframeKey(prev => prev + 1)
        }
    }, [open])
    const { t, i18n } = useTranslation()
    return <Modal
        visible={open}
        zIndex={1001}
        width={1400}
        title={t('techNews') + (i18n.language !== 'zh' ? ' ' : '') 
            + t('content')}
        onCancel={close}
        maskClosable
        footer={null}
    >
        {loading ? <Skeleton active />
            : (url
                ? <a href={sourceUrl} target='_blank' style={{ display: 'block', width: '100%', textAlign: 'center', overflowY: 'auto' }}>
                    <Space direction='vertical'>
                        <Image src={url} width='100%' preview={false} placeholder={<Spin />} style={{ maxWidth: 1200 }} />
                        <div style={{ textAlign: 'center' }}>点击查看原新闻</div>
                    </Space>
                </a>
                : (
                    tokens?.map((token: any, index: number) => {
                        const newDefault = {
                            ...defaults
                        }
                        const htmlStr = marked.parse(token.raw, {
                            ...newDefault,
                            gfm: true,
                            breaks: true,
                            renderer
                        })
                        return <div key={`${index}`} className='token_box2'
                            dangerouslySetInnerHTML={{
                                __html: (htmlStr as string).replace(/\\n/g, '<br/>')
                            }}
                        />
                    })
                )
            )
        }
    </Modal>
}

const ReportInfo: FC = () => {
    const history = useHistory()
    const dispatch = useDispatch()
    const { t, i18n } = useTranslation()
    const [data, setData] = useState<any[]>([])
    const [dataLoading, setDataLoading] = useState(false)
    const [isAll, setIsAll] = useState(false)
    const [startDate, setStartDate] = useState<Moment | undefined>(moment(new Date()))
    const [endDate, setEndDate] = useState<Moment | undefined>()
    const [reportLs, setReportLs] = useState<any[]>([])
    const reportLsAll = useRef<any[]>([])
    // 当前选中新闻项
    const [currentItem, setCurrentItem] = useState<TableDataType | undefined>()
    const [loading, setLoading] = useState(false)
    const [modalLoading, setModalLoading] = useState(false)
    const [open, setOpen] = useState(false)
    // 评论列表弹窗
    const [tableListOpen, setTableListOpen] = useState(false)
    const [html, setHtml] = useState<any>()
    const [url, setUrl] = useState<string | undefined>()
    const [sourceUrl, setSourceUrl] = useState<string | undefined>()
    const [workGroupId, ] = useState<number | undefined>()
    const [pageInfo, setPageInfo] = useState({
        pageNo: 1,
        pageSize: 10,
        total: 0
    })

    const [routes, ] = useState([
        {
            path: '/indexPage',
            breadcrumbName: t('indexPage'),
        },
        {
            path: '/voteReport',
            breadcrumbName: t('TMTTechBack'),
        },
    ])

    const columns: TableProps<TableDataType>['columns'] = [
        {
            title: '评论人',
            dataIndex: 'creator',
            key: 'creator',
        },
        {
            title: '评论时间',
            dataIndex: 'createTime',
            key: 'createTime',
            render: (val) => moment(val).format('YYYY-MM-DD'),
        },
        {
            title: '是否赞成',
            dataIndex: 'isApproval',
            key: 'isApproval',
            render: (val) => val
                ? <span style={{ color: 'green' }}>是</span>
                : <span style={{ color: 'red' }}>否</span>,
        },
        {
            title: '描述',
            dataIndex: 'remark',
            key: 'remark',
        },
        {
            title: '操作',
            dataIndex: 'op',
            key: 'op',
            render: (_, record) => <a href={record.techUrl} target="_blank">查看原新闻</a>,
        },
    ]

    const goAddVote = () => {
        history.push('/addVote')
    }
    const onClickAll = () => {
        setIsAll(true)
        setTableListOpen(true)
        setReportLs(reportLsAll.current)
    }
    const viewPage = async (tokens: any[], record?: TableDataType) => {
        setOpen(true)
        if (record) {
            setModalLoading(true)
            const res = await getTechInfo({
                id: record.id
            }).finally(() => setModalLoading(false))
            setUrl(res.imageInfo)
            setSourceUrl(res.techUrl)
        } else {
            setUrl('')
        }
        console.log('tokens: ', tokens)
        setHtml(tokens)
    }
    const closeView = () => {
        setOpen(false)
    }
    const closeTableList = () => {
        setTableListOpen(false)
    }

    const getTechInfoListFn = useCallback(() => {
        setDataLoading(true)
        getTechInfoList({
            ...pageInfo,
            workGroupId
        }).then((res) => {
            res.content.forEach(item => {
                item.techAbstract = item.techAbstract
                    ? item.techAbstract.replace(/"/g, '').replace(/`/g, '').replace(/markdown/g, '')
                        .replace(/\s{2}-/g, '   -').replace(/\\n#/g, '\n #').replace(/\\n-/g, '\n -').replace(/\\n/g, '\n')
                    : ''
                console.log('item.techAbstract: ', item.techAbstract)
                const $tokens = marked.lexer(item.techAbstract)
                item.$tokens = $tokens
            })
            setData(res.content)
            setPageInfo({
                ...pageInfo,
                total: res.totalElements
            })
        }).finally(() => {
            setDataLoading(false)
        })
    }, [workGroupId, pageInfo])

    // 获取指定条的反馈列表
    const getFaceBackListFn = async (id: string) => {
        setIsAll(false)
        setLoading(true)
        setTableListOpen(true)
        setReportLs([])
        setCurrentItem(data.find(item => item.id === id))
        const faceBackList = await getFaceBackList({
            id: Number(id)
        }).finally(() => {
            setLoading(false)
        })
        if (faceBackList.length > 0) {
            setReportLs(faceBackList.map((item: any) => {
                const { id, creator, createTime, remark, isApproval } = item
                return {
                    id,
                    creator,
                    createTime,
                    techAbstract: item.parent.techAbstract,
                    techUrl: item.parent.techUrl,
                    isApproval,
                    remark
                }
            }))
        }
    }

    const onChangeStartDate: DatePickerProps['onChange'] = (date) => {
        setStartDate(date || undefined)
    }
    const onChangeEndDate: DatePickerProps['onChange'] = (date) => {
        setEndDate(date || undefined)
    }
    const getFaceBackReportFn = useCallback(async () => {
        console.log('startDate: ', startDate)
        if (!startDate) {
            return
        }
        // 获取所有反馈总报告
        setLoading(true)
        const faceBackReport = await getFaceBackReport({
            startDate: startDate?.format('YYYY-MM-DD') ?? '',
            endDate: endDate?.format('YYYY-MM-DD') ?? ''
        }).finally(() => {
            setLoading(false)
        })
        reportLsAll.current = faceBackReport
        setReportLs(faceBackReport)
    }, [startDate, endDate])

    useEffect(() => {
        getTechInfoListFn()
    }, [workGroupId])
    useEffect(() => {
        getTechInfoListFn()
    }, [pageInfo.pageNo, pageInfo.pageSize])
    useEffect(() => {
        // 获取总报告
        getFaceBackReportFn()
    }, [startDate, endDate])
    return (<>
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <Nav dispatch={dispatch} />
            <PageBreadcrumb routerParam={routes}
                history={history}
                dispatch={dispatch}
            />
            <Card title={<span>{t('techNews')}<span style={{ fontSize: 13, color: '#aaa' }}>({t('prompt15')})</span></span>}
                className="edit_table" style={{ paddingTop: 0 }}
                extra={<Space>
                    <Button type="primary" ghost onClick={goAddVote}>{t('publish') + (i18n.language !== 'zh' ? ' ' : '') 
                        + t('techNews')}</Button>
                    <Button type="primary" ghost onClick={onClickAll}>
                        {t('showAll') + (i18n.language !== 'zh' ? ' ' : '')
                        + t('report')}
                    </Button>
                </Space>}
            >
                <List
                    dataSource={data}
                    loading={dataLoading}
                    pagination={{
                        ...pageInfo,
                        current: pageInfo.pageNo,
                        onChange: (page: number) => {
                            console.log('page: ', page)
                            setPageInfo({
                                ...pageInfo,
                                pageNo: page
                            })
                        },
                    }}
                    renderItem={item => (
                        <List.Item
                            actions={[<a
                                onClick={() => {
                                    getFaceBackListFn(item.id)
                                }}
                            >查看技术报告</a>]}
                        >
                            <Descriptions className='comment' column={{ xs: 1, sm: 1, md: 6 }}>
                                <Descriptions.Item label="标题"><a
                                    onClick={(e) => {
                                        // 阻止事件冒泡
                                        e.stopPropagation()
                                        viewPage(item.techUrl ? [] :
                                            marked.lexer(item.techContext.includes('obj') ? getHtmlStr(JSON.parse(item.techContext).str) : '')
                                        , item)
                                    }}
                                >{item.techTitle}</a></Descriptions.Item>
                                <Descriptions.Item label="创建人">{item.creator}</Descriptions.Item>
                                <Descriptions.Item label="创建时间">{moment(item.createTime).format('YYYY-MM-DD')}</Descriptions.Item>
                                <Descriptions.Item label="文章摘要">
                                    <a title={getHtmlStr(item.techAbstract)}
                                        onClick={(e) => {
                                            // 阻止事件冒泡
                                            e.stopPropagation()
                                            viewPage(item.$tokens)
                                        }}
                                    >
                                        {getHtmlStr(item.techAbstract).length > 50
                                            ? (getHtmlStr(item.techAbstract).substring(0, 50) + '...')
                                            : getHtmlStr(item.techAbstract)
                                        }
                                    </a>
                                    {/* {item.$tokens?.map((token: any, index: number, arr: any[]) => {
                                        const newDefault = {
                                            ...defaults
                                        }
                                        const htmlStr = marked.parse(token.raw, {
                                            ...newDefault,
                                            gfm: true,
                                            breaks: true,
                                            renderer
                                        })
                                        return <div key={`${index}`}>
                                            <Popover placement="top" title={null}
                                                content={<div
                                                    style={{ maxWidth: 800, whiteSpace: 'normal' }}
                                                >{htmlStr as string}</div>}
                                            >
                                                <a
                                                    onClick={(e) => {
                                                        // 阻止事件冒泡
                                                        e.stopPropagation()
                                                        viewPage(arr)
                                                    }}
                                                >
                                                    {(getHtmlStr(htmlStr as string).length > 50
                                                        ? getHtmlStr((htmlStr as string)).substring(0, 50) + '...'
                                                        : getHtmlStr((htmlStr as string)))
                                                    }
                                                </a>
                                            </Popover>
                                        </div>
                                    })} */}
                                </Descriptions.Item>
                            </Descriptions>
                        </List.Item>
                    )}
                />
            </Card>
            <Modal title="技术报告" visible={tableListOpen}
                width='90%'
                onCancel={closeTableList}
                maskClosable
                footer={null}
                className='comment_model'
            >
                {!isAll
                    ? (<>
                        <div className='desc'>
                            {currentItem?.$tokens?.map((token: any, index: number) => {
                                const newDefault = {
                                    ...defaults
                                }
                                const htmlStr = marked.parse(token.raw, {
                                    ...newDefault,
                                    gfm: true,
                                    breaks: true,
                                    renderer
                                })
                                return <div key={`${index}`} className='token_box2'
                                    dangerouslySetInnerHTML={{
                                        __html: (htmlStr as string).replace(/\\n/g, '<br/>')
                                    }}
                                />
                            }) ?? ''}
                        </div>
                        <Table columns={columns} dataSource={reportLs} loading={loading}
                            bordered rowKey='id'
                            locale={{ emptyText: isAll && !startDate
                                ? t('Please Select') + (i18n.language !== 'zh' ? ' ' : '')
                            + t('startDate')
                                : t('Null Value')
                            }}
                        />
                    </>)
                    : <>
                        {/* 所有评论汇总 */}
                        <Space style={{ marginTop: 20, justifyContent: 'flex-end', width: '100%' }}>
                            <DatePicker value={startDate} allowClear={false}
                                onChange={onChangeStartDate}
                                placeholder={t('Please Select') + (i18n.language !== 'zh' ? ' ' : '')
                            + t('startDate')}
                            />
                            <DatePicker value={endDate} onChange={onChangeEndDate}
                                placeholder={t('Please Select') + (i18n.language !== 'zh' ? ' ' : '')
                            + t('endDate')}
                            />
                        </Space>
                        <List className='report_info'
                            dataSource={reportLs}
                            loading={loading}
                            pagination={{
                                position: 'bottom'
                            }}
                            locale={{ emptyText: isAll && !startDate
                                ? t('Please Select') + (i18n.language !== 'zh' ? ' ' : '')
                            + t('startDate')
                                : t('Null Value')
                            }}
                            renderItem={item => (
                                <List.Item>
                                    <Descriptions className='comment' column={{ xs: 1, sm: 1, md: 5 }}>
                                        <Descriptions.Item label="标题"
                                            contentStyle={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                        >
                                            <span title={item.techTitle}>{item.techTitle}</span>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="认可的数量">{item.approveCount}</Descriptions.Item>
                                        <Descriptions.Item label="不认可的数量">{item.rejectCount}</Descriptions.Item>
                                        <Descriptions.Item label="文章摘要">
                                            <a title={getHtmlStr(item.techAbstract)}
                                                onClick={(e) => {
                                                    // 阻止事件冒泡
                                                    e.stopPropagation()
                                                    viewPage(item.$tokens)
                                                }}
                                            >
                                                {getHtmlStr(item.techAbstract).length > 50
                                                    ? (getHtmlStr(item.techAbstract).substring(0, 50) + '...')
                                                    : getHtmlStr(item.techAbstract)
                                                }
                                            </a>
                                        </Descriptions.Item>
                                    </Descriptions>
                                </List.Item>
                            )}
                        />
                    </>
                }
            </Modal>
        </Space>
        <ViewHtml open={open} html={html} url={url} sourceUrl={sourceUrl} close={closeView} loading={modalLoading} />
    </>)
}
export default ReportInfo
